import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import {
  approveOnboardingCEO,
  approveOnboardingCTO,
} from "../../services/api/onboarding";
import { toast } from "sonner";
import Modal from "../../components/Modal";

const ApproveOnboarding: React.FC = () => {
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get("Id");
  const role = searchParams.get("role")?.toLowerCase();

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);

  const REJECTION_REASONS = [
    "Incomplete Documentation",
    "Incorrect Company Information",
    "Invalid Domain/Email",
    "Other",
  ];

  const handleApprovalAction = async (
    approved: boolean,
    reason: string[] = [],
  ) => {
    if (!organizationId || !role) {
      setStatus("error");
      setMessage("Invalid approval link. Missing organization ID or role.");
      return;
    }

    if (role !== "ceo" && role !== "cto") {
      setStatus("error");
      setMessage("Invalid role specified in the approval link.");
      return;
    }

    setLoading(true);
    setStatus("idle");

    try {
      if (role === "ceo") {
        await approveOnboardingCEO(organizationId, approved, reason);
      } else {
        await approveOnboardingCTO(organizationId, approved, reason);
      }
      setStatus("success");
      setMessage(
        approved
          ? `Onboarding has been approved by the ${role.toUpperCase()}.`
          : `Onboarding has been rejected by the ${role.toUpperCase()}.`,
      );
    } catch (error: any) {
      setStatus("error");
      setMessage(
        error?.response?.data?.message ||
          error?.message ||
          "An unexpected error occurred.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Automatically trigger approval if parameters are present?
    // For safety, maybe a button is better, but email links often auto-trigger.
    // Let's provide a button for explicit action to avoid accidental triggers.
  }, []);

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4 font-abel">
      {/* Background Decorative Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-100 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-50 rounded-full blur-3xl opacity-60" />
      </div>

      <div className="max-w-md w-full relative z-10">
        <div className="bg-white/80 backdrop-blur-xl border border-white rounded-[2rem] shadow-[0_20px_50px_-20px_rgba(0,0,0,0.1)] p-8 md:p-10 text-center animate-in fade-in zoom-in duration-700">
          {/* Header Icon */}
          <div className="mb-8 flex justify-center">
            {status === "success" ? (
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center border border-green-100 shadow-sm animate-bounce-short">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </div>
            ) : status === "error" ? (
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center border border-red-100 shadow-sm animate-shake">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
            ) : (
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center border border-blue-100 shadow-sm">
                {role === "ceo" ? (
                  <ShieldCheck className="w-10 h-10 text-blue-500" />
                ) : (
                  <UserCheck className="w-10 h-10 text-blue-500" />
                )}
              </div>
            )}
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-4 tracking-tight">
            Onboarding Approval
          </h1>

          <p className="text-gray-500 mb-8 leading-relaxed">
            {status === "idle"
              ? `You are about to approve the onboarding process for your organization as the ${role?.toUpperCase()}.`
              : message}
          </p>

          {status === "idle" && (
            <div className="flex flex-col gap-3">
              <button
                onClick={() => handleApprovalAction(true)}
                disabled={loading}
                className="w-full py-4 bg-gray-900 text-white rounded-2xl font-semibold shadow-lg shadow-gray-200 hover:bg-gray-800 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Confirm Approval
                    <CheckCircle2 className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>

              <button
                onClick={() => setIsRejectModalOpen(true)}
                disabled={loading}
                className="w-full py-4 bg-white text-red-600 border border-red-100 rounded-2xl font-semibold hover:bg-red-50 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Reject Onboarding
                    <XCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </>
                )}
              </button>
            </div>
          )}

          {status !== "idle" && (
            <div className="mt-6 pt-6 border-t border-gray-100 text-sm text-gray-400">
              {status === "success"
                ? "This process is complete. You may close this window."
                : "Please contact support if you believe this is an error."}
            </div>
          )}
        </div>

        {/* Footer branding */}
        {/* <div className="mt-8 text-center text-gray-400 text-sm">
          Powered by 25th & Staffing Onboarding System
        </div> */}
      </div>

      {/* Rejection Reasons Modal */}
      <Modal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
      >
        <div className="p-1">
          <div className="mb-6 flex justify-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center border border-red-100 shadow-sm">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            Reason for Rejection
          </h2>
          <p className="text-gray-500 mb-6 text-center text-sm">
            Please select the reason(s) for rejecting this onboarding request.
          </p>

          <div className="space-y-3 mb-8">
            {REJECTION_REASONS.map((reason) => (
              <label
                key={reason}
                className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedReasons.includes(reason)
                    ? "border-red-500 bg-red-50/50"
                    : "border-gray-100 hover:border-gray-200"
                }`}
              >
                <input
                  type="checkbox"
                  className="hidden"
                  checked={selectedReasons.includes(reason)}
                  onChange={() => {
                    setSelectedReasons((prev) =>
                      prev.includes(reason)
                        ? prev.filter((r) => r !== reason)
                        : [...prev, reason],
                    );
                  }}
                />
                <div
                  className={`w-5 h-5 rounded-md border-2 mr-3 flex items-center justify-center transition-all ${
                    selectedReasons.includes(reason)
                      ? "bg-red-500 border-red-500"
                      : "border-gray-300"
                  }`}
                >
                  {selectedReasons.includes(reason) && (
                    <CheckCircle2 className="w-3 h-3 text-white" />
                  )}
                </div>
                <span
                  className={`font-medium ${
                    selectedReasons.includes(reason)
                      ? "text-red-900"
                      : "text-gray-700"
                  }`}
                >
                  {reason}
                </span>
              </label>
            ))}
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                if (selectedReasons.length === 0) {
                  toast.error("Please select at least one reason.");
                  return;
                }
                setIsRejectModalOpen(false);
                handleApprovalAction(false, selectedReasons);
              }}
              disabled={loading}
              className="w-full py-4 bg-red-600 text-white rounded-2xl font-semibold shadow-lg shadow-red-100 hover:bg-red-700 transition-all active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Confirm Rejection"
              )}
            </button>
            <button
              onClick={() => setIsRejectModalOpen(false)}
              className="w-full py-3 text-gray-500 font-medium hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ApproveOnboarding;
